import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import PasswordValidator from 'password-validator';
import { type FormikHelpers, useFormik } from 'formik';
import { boolean, date, object, ref, type Schema, string } from 'yup';
import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import { StepperFormContent } from './StepperFormContent';
import { type PeripheralProp, type StepperFormContentValues } from './types';
import { PAGE_TEXT } from './lang';
import { IDENTITY_TYPE, IDENTITY_DOCUMENT_TYPE } from './constants';
import { useVerifyEmail } from '../graphql';
import { Alert, type AlertDetails, Button } from '../../../components';
import { handleReadySubmit } from '../../../utils';

// Updated by 30/07/2024 Meeting
// to calculate the length of a string
// function calculateLength(str: string): number {
//   let length = 0;
//   for (let i = 0; i < str.length; i++) {
//     length += str.charCodeAt(i) > 255 ? 2 : 1;
//   }
//   return length;
// }

const peripheralProp: PeripheralProp = {
  emailUI: {
    usedEmail: [],
    addUsedEmail(email: string) {
      return this.usedEmail.push(email);
    },
    isUsedEmail(email: string) {
      return this.usedEmail.includes(email);
    },
  },
  sendCodeUI: {
    disableSendCode: true,
    setDisableSendCode(bool: boolean) {
      this.disableSendCode = bool;
    },
    canSendCode() {
      return this.disableSendCode;
    },
  },
};

const { emailUI, sendCodeUI } = peripheralProp;

const pwdSchema = new PasswordValidator();

pwdSchema
  .is()
  .min(8)
  .is()
  .max(20)
  .has()
  .letters() // Must have letters
  .has()
  .digits() // Must have digits
  .has()
  .symbols(); // Must have symbols

const HKG = 'HKG';

const validationSchema: Record<number, Schema> = {
  0: object({
    email: string()
      .email(PAGE_TEXT.VALID_EMAIL)
      .required(PAGE_TEXT.REQUIRED)
      .test(
        'is-used-email',
        PAGE_TEXT.EMAIL_HAS_BEEN_REGISTERED,
        value => !emailUI.isUsedEmail(value),
      ),
    emailVerificationCode: string().required(PAGE_TEXT.REQUIRED),
    password: string()
      .required(PAGE_TEXT.REQUIRED)
      .test(
        'is-strong-password',
        PAGE_TEXT.VALID_PASSWORD,
        value => pwdSchema.validate(value) === true,
      ),
    confirmPassword: string()
      .oneOf([ref('password')], PAGE_TEXT.PASSWORD_MATCH)
      .required(PAGE_TEXT.REQUIRED),
  }),
  2: object({
    surname: string()
      .required(PAGE_TEXT.REQUIRED)
      // [RPG-1924] only English letters (upper/lower case) and spaces allowed
      // prohibits accented characters (à, é, ü, etc.)
      .matches(/^[A-Za-z\s]+$/, 'English letters only')
      .test('byte-length', 'Exceeds maximum length of 30 characters', value => {
        return value.length <= 30;
      }),
    givenName: string()
      .required(PAGE_TEXT.REQUIRED)
      .matches(/^[A-Za-z\s]+$/, 'English letters only')
      .test('byte-length', 'Exceeds maximum length of 30 characters', value => {
        return value?.length <= 30;
      }),
    chineseName: string()
      // [RPG-1924] strict Chinese character validation (simplified/traditional)
      // \u4e00-\u9fff covers most Chinese characters (including common extensions)
      .matches(/^[\u4e00-\u9fff]*$/, PAGE_TEXT.CHINESE_NAME_REQUIRED)
      // prevent mixed Chinese-English names
      .test('no-mixed', PAGE_TEXT.CHINESE_NAME_REQUIRED, value => {
        return !/[A-Za-z]/.test(value ?? '');
      })
      .test('maxlength', 'Exceeds maximum length of 10 characters', value => {
        return (value ?? '')?.length <= 10;
      }),
    dateOfBirth: date().required(PAGE_TEXT.REQUIRED),
    gender: string().required(PAGE_TEXT.REQUIRED),
    identityType: string().required(PAGE_TEXT.REQUIRED),
    identityDocumentType: string().required(PAGE_TEXT.REQUIRED),
    nationality: string().required(PAGE_TEXT.REQUIRED),
    province: string().when('nationality', ([nationality], schema) => {
      const isChina: boolean = nationality === 'CHN';
      return isChina ? schema.required('Required') : schema.notRequired();
    }),
    identityDocumentNumber: string().when(
      ['identityDocumentType', 'nationality'],
      ([identityDocumentType, nationality], schema) => {
        if (
          identityDocumentType === IDENTITY_DOCUMENT_TYPE.HK_ID ||
          nationality === HKG
        ) {
          return schema
            .required(PAGE_TEXT.REQUIRED)
            .matches(
              /^[A-Z]{1,2}[0-9]{6}\([0-9A]\)$/,
              'Invalid HKID format (e.g., A123456(7))',
            );
        }
        return schema.required(PAGE_TEXT.REQUIRED);
      },
    ),
    mobileNumber: string()
      .min(5, PAGE_TEXT.REQUIRED)
      .required(PAGE_TEXT.REQUIRED),
    addressCountryRegion: string().required(PAGE_TEXT.REQUIRED),
    addressLine1: string()
      .required(PAGE_TEXT.REQUIRED)
      .test('byte-length', 'Exceeds maximum length of 32 characters', value => {
        return value.length <= 32;
      }),
    addressLine2: string()
      .nullable()
      .test('byte-length', 'Exceeds maximum length of 32 characters', value => {
        return String(value).length <= 32;
      }),
    addressLine3: string()
      .nullable()
      .test('byte-length', 'Exceeds maximum length of 32 characters', value => {
        return String(value).length <= 32;
      }),
    disabilitySpecialType: string().required(PAGE_TEXT.REQUIRED),
    disabilitySpecialDetail: string().when(
      'disabilitySpecialType',
      ([disabilitySpecialType], schema) => {
        const detailsRequired: boolean =
          Boolean(disabilitySpecialType) && disabilitySpecialType !== 'NA';
        return detailsRequired
          ? schema.required(PAGE_TEXT.REQUIRED)
          : schema.notRequired();
      },
    ),
    PIDeclaration: boolean().required(PAGE_TEXT.REQUIRED).oneOf([true]),
  }),
};

export type SignupFormProps = {
  activeStep: number;
  isLastStep: boolean;
  onBack: () => void;
  onSubmit: (
    values: StepperFormContentValues,
    helper: FormikHelpers<StepperFormContentValues>,
  ) => void;
};

export type SignupFormValues = StepperFormContentValues;

export const SignupForm: React.FunctionComponent<SignupFormProps> = (
  props: SignupFormProps,
) => {
  const { activeStep, isLastStep, onSubmit: handleSubmit, onBack } = props;
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const initialValues: StepperFormContentValues = {
    email: '',
    emailVerificationCode: '',
    password: '',
    confirmPassword: '',
    surname: '',
    givenName: '',
    chineseName: '',
    dateOfBirth: '',
    gender: '',
    identityType: '',
    identityDocumentType: '',
    nationality: '',
    province: '',
    identityDocumentNumber: '',
    mobileNumber: '',
    telephoneNumber: '',
    addressCountryRegion: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    disabilitySpecialType: '',
    disabilitySpecialDetail: '',
    PIDeclaration: false,
    isLocal: false,
    needStudentVisa: false,
  };

  // pass required value
  const initialStatus: Partial<
    Record<keyof StepperFormContentValues, boolean>
  > = {
    email: true,
    emailVerificationCode: true,
    password: true,
    confirmPassword: true,
    surname: true,
    givenName: true,
    chineseName: false,
    dateOfBirth: true,
    gender: true,
    identityType: true,
    identityDocumentType: true,
    nationality: true,
    province: false,
    identityDocumentNumber: true,
    mobileNumber: true,
    telephoneNumber: true,
    addressCountryRegion: true,
    addressLine1: true,
    addressLine2: false,
    addressLine3: false,
    disabilitySpecialType: false,
    disabilitySpecialDetail: false,
    PIDeclaration: true,
    isLocal: false,
    needStudentVisa: false,
  };

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const formik = useFormik<StepperFormContentValues>({
    initialValues,
    initialStatus,
    validationSchema: validationSchema[activeStep],
    onSubmit: async (
      values: StepperFormContentValues,
      helper: FormikHelpers<StepperFormContentValues>,
    ): Promise<void> => {
      if (activeStep === 0) {
        const { email, emailVerificationCode } = values;

        await useVerifyEmail(client, email, emailVerificationCode)
          .then(async json => {
            const { code } = json;
            switch (code) {
              case 'EV-V1':
              case 'EV-V2':
                await formik.setFieldTouched('emailVerificationCode', true);
                formik.setErrors({
                  emailVerificationCode: PAGE_TEXT.INCORRECT_VERIFICATION_CODE,
                });
                break;
              case 'EV-V3':
                // it means email has been registered, err msg was handled in Yup
                if (!emailUI.isUsedEmail(formik.values.email)) {
                  emailUI.addUsedEmail(formik.values.email);
                  sendCodeUI.setDisableSendCode(true);
                }
                await formik.setFieldTouched('email', true);
                break;
              case 'EV-VS':
                // email verification goes fine, let it pass to step 2
                await formik.setFieldTouched('email', false);
                await formik.setFieldTouched('emailVerificationCode', false);
                handleSubmit(values, helper);
                break;
              default:
                throw new Error('Internet Error');
            }
          })
          .catch(err => {
            setAlertDetails({
              alertContent: `Error Occurs. ${(err as Error)?.message}`,
              alertType: 'error',
            });
          });
      } else {
        handleSubmit(values, helper);
      }
    },
  });

  const hasValidIdentity =
    formik.values.identityType !== '' &&
    formik.values.identityType !== IDENTITY_TYPE.OTHERS;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '3rem',
        width: '100%',
      }}
    >
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: '35px',
          width: '100%',
        }}
        onSubmit={handleReadySubmit(formik)}
        // prevent browser validate, otherwise will block formik setTouched when submitting
        noValidate
      >
        <div className="w-3/4 flex justify-center">
          <StepperFormContent
            activateStep={activeStep}
            formik={formik}
            peripheralProp={peripheralProp}
          />
        </div>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={onBack}
            sx={{ mr: 1 }}
          >
            {PAGE_TEXT.BACK}
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            type="submit"
            variant="contained"
            disabled={activeStep === 1 && !hasValidIdentity}
          >
            {isLastStep ? PAGE_TEXT.SUBMIT : PAGE_TEXT.NEXT}
          </Button>
        </Box>
      </form>
      <Link to="/login" style={{ marginTop: '15px' }}>
        {PAGE_TEXT.ALREADY_HAVE_AN_ACCOUNT}
      </Link>
    </div>
  );
};
